import { io } from 'socket.io-client';

// One socket factory, two token universes.
//
// Staff and client sessions use different storage keys, different signing
// algorithms and different verifiers on the server (see utils/clientAuth.js).
// This file's only job is to hand the right token to the right handshake and
// never mix them — which is why `kind` is required rather than inferred: a
// firm employee opening the portal in the same browser has BOTH tokens in
// localStorage, and guessing would pick the wrong one.

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const TOKEN_KEY = { staff: 'accessToken', client: 'portalAccessToken' };

export function createChatSocket(kind) {
  const key = TOKEN_KEY[kind];
  if (!key) throw new Error(`Unknown chat socket kind: ${kind}`);

  return io(API_URL, {
    path: '/socket.io',
    // A FUNCTION, not an object: socket.io calls it again on every reconnect
    // attempt, so a token refreshed by the axios interceptor while we were
    // disconnected is picked up automatically. A frozen object would keep
    // presenting the expired token and reconnect forever.
    auth: (cb) => cb({ token: localStorage.getItem(key), kind }),
    // Websocket first, long-polling as the fallback that keeps chat working
    // behind corporate proxies that kill upgrades — the exact network a CA
    // firm's office sits on.
    transports: ['websocket', 'polling'],
    reconnectionDelay: 800,
    reconnectionDelayMax: 8000,
    timeout: 12000,
    autoConnect: true,
  });
}

// Promise wrapper for socket.io's ack callbacks, with a timeout so a dropped
// connection surfaces as a failed send rather than a spinner that never ends.
export function emitAck(socket, event, payload, timeoutMs = 12000) {
  return new Promise((resolve) => {
    if (!socket || !socket.connected) return resolve({ error: 'offline' });
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) { settled = true; resolve({ error: 'timeout' }); }
    }, timeoutMs);
    socket.emit(event, payload, (res) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(res || {});
    });
  });
}
